import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview'
import ExportMenu, { ExportFormat } from '../../../components/novarium/ExportMenu'
import NvTooltips from '../../../components/novarium/NvTooltips'
import {
  UrgencyDailyPointDTO,
  UrgencyOptionDTO,
  UrgencySummaryRequestDTO,
} from '../../../dto/urgencySummary/UrgencySummaryDTO'
import {
  exportUrgencySummary,
  getUrgencyHourly,
  getUrgencyPriorities,
  getUrgencyWhere,
} from '../../../services/UrgencySummaryController'
import { hasArrivals, periodReference, UrgencyMeasure } from '../urgencyAnalysis'
import { UrgencyDetailRouteState, UrgencyDrill } from '../urgencyDetailState'
import { ARRIVAL_STATE_GROUPS } from '../urgencyStateGroups'
import { URGENCY_TAB } from '../urgencyTabs'
import useSectionData from '../useSectionData'
import { UrgencySummaryData } from '../useUrgencySummaryData'
import UrgencyDaySidebar from './UrgencyDaySidebar'
import UrgencyEvolution from './UrgencyEvolution'
import UrgencyFilters from './UrgencyFilters'
import UrgencyGroupedTable, { UrgencyGroupBy } from './UrgencyGroupedTable'
import UrgencyHourlyTab from './UrgencyHourlyTab'
import UrgencyIndicators from './UrgencyIndicators'
import UrgencyMeasureSelector from './UrgencyMeasureSelector'
import UrgencyPrioritiesTab from './UrgencyPrioritiesTab'
import UrgencyWhere from './UrgencyWhere'

interface UrgencySummaryPageProps {
  data: UrgencySummaryData
  groupBy: UrgencyGroupBy
  onGroupByChange: (groupBy: UrgencyGroupBy) => void
  /** Pestaña elegida bajo el ranking y medida compartida por la evolución y «Por hora»: viven en el contenedor de la ruta. */
  tab: number
  onTabChange: (tab: number) => void
  measure: UrgencyMeasure
  onMeasureChange: (measure: UrgencyMeasure) => void
  /** Ruta del resumen; el detalle es `${basePath}/detail`. */
  basePath: string
}

/** El nombre de una opción, o `null` si no hay id o las opciones todavía no llegaron. */
function nameOf(list: UrgencyOptionDTO[] | undefined, id: number | null): string | null {
  return id === null ? null : list?.find((option) => option.id === id)?.name ?? null
}

/** Lo que identifica una consulta (período y filtros): clave del caché de las secciones. */
const requestKey = (request: UrgencySummaryRequestDTO): string =>
  `${request.from}|${request.to}|${request.templateId}|${request.priorityId}|${request.financierId}`

export default function UrgencySummaryPage({
  data, groupBy, onGroupByChange, tab, onTabChange, measure, onMeasureChange, basePath,
}: UrgencySummaryPageProps) {
  const intl = useIntl()
  const navigate = useNavigate()
  const {
    filters, options, summary, applied, status, busy, errorMessage, retry, patchFilters, rangeOrigin, returnToPreviousRange,
    viewRange,
  } = data

  const hasChips = filters.templateId !== null || filters.financierId !== null || filters.priorityId !== null
  const hasData = summary !== null && applied !== null && status !== 'error'
  const isEmpty = hasData && summary.current.arrivals === 0
  const showContent = hasData && !isEmpty

  // «Guardia» no se ofrece si ya se eligió una guardia (todo es de esa guardia) ni si el sitio tiene una sola
  const byTemplate = applied?.templateId === null && (options?.templates.length ?? 0) > 1
  const availableGroups = useMemo<UrgencyGroupBy[]>(
    () => [...(byTemplate ? ['template' as const] : []), 'financier', 'professional', 'state'],
    [byTemplate],
  )
  const shownGroupBy = availableGroups.includes(groupBy) ? groupBy : availableGroups[0]

  // ── Secciones con su propia consulta: la de «Dónde y cuándo» siempre; las de las pestañas, recién al abrirlas ──
  const appliedKey = showContent && applied ? requestKey(applied) : null
  const required = (): UrgencySummaryRequestDTO => {
    if (!applied) throw new Error('Resumen sin consultar')
    return applied
  }
  const where = useSectionData(appliedKey === null ? null : `${appliedKey}|where`, (signal) => getUrgencyWhere(required(), signal))
  const hourly = useSectionData(
    appliedKey !== null && tab === URGENCY_TAB.hourly ? `${appliedKey}|hourly` : null,
    (signal) => getUrgencyHourly(required(), signal),
  )
  const priorities = useSectionData(
    appliedKey !== null && tab === URGENCY_TAB.priorities ? `${appliedKey}|priorities` : null,
    (signal) => getUrgencyPriorities(required(), signal),
  )

  // ── Panel lateral del día ──
  const [detailDay, setDetailDay] = useState<UrgencyDailyPointDTO | null>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  /** Abre el detalle de un día con llegadas; al cerrarlo el foco vuelve a `opener` (el gráfico o el chip). */
  const openDay = useCallback((day: UrgencyDailyPointDTO, opener: HTMLElement | null) => {
    openerRef.current = opener
    setDetailDay(day)
  }, [])
  const closeDetail = useCallback(() => {
    setDetailDay(null)
    const opener = openerRef.current
    openerRef.current = null
    // Cuando el panel ya se cerró; si el gráfico o el chip ya no están, no hay a dónde volver
    if (opener) window.setTimeout(() => { if (opener.isConnected) opener.focus() }, 0)
  }, [])
  /** «Ver solo este día» (con una guardia abierta en el panel, «y esta guardia»): pasan a ser el período y el filtro de la pantalla. */
  const viewDay = useCallback((date: string, templateId: number | null) => {
    viewRange(date, date, templateId === null ? undefined : templateId)
    closeDetail()
  }, [viewRange, closeDetail])
  // Contra qué se compara el día (y una guardia) en el panel: el promedio del período de la pantalla
  const dayReference = useMemo(() => (summary ? periodReference(summary.current, summary.daily) : null), [summary])
  const activeDays = useMemo(() => (summary ? summary.daily.filter(hasArrivals).length : 0), [summary])

  /** Abre el detalle con los filtros aplicados en la última consulta (no con lo que se está editando). */
  function openDetail(drill: UrgencyDrill) {
    if (!applied) return
    const templateId = drill.templateId !== undefined ? drill.templateId : applied.templateId
    // El financiador filtra toda la consulta: el detalle lo conserva salvo que la fila abierta sea de otro financiador
    const financierId = drill.financierId !== undefined ? drill.financierId : applied.financierId
    // Guardia y financiador con nombre pasan a ser filtros de la tabla del detalle (visibles y quitables ahí); sin el nombre
    // (opciones todavía sin cargar) viajan como criterio fijo
    const templateName = drill.templateName ?? nameOf(options?.templates, templateId)
    const financierName = drill.noFinancier ? null : drill.financierName ?? nameOf(options?.financiers, financierId)
    const state: UrgencyDetailRouteState = {
      filters: {
        from: applied.from,
        to: applied.to,
        templateId: templateName ? null : templateId,
        priorityId: null,
        financierId: drill.noFinancier || financierName ? null : financierId,
        noFinancier: drill.noFinancier ?? false,
        noProfessional: drill.noProfessional ?? false,
        // El estado, el código de triaje y los duplicados son filtros de la tabla (ver `seed`), no criterios fijos
      },
      seed: {
        guardia: templateName,
        financierName,
        evolutionProfessional: drill.professionalName ?? null,
        stateGroups: [...(drill.stateGroups ?? ARRIVAL_STATE_GROUPS)],
        priorityId: applied.priorityId,
      },
    }
    navigate(`${basePath}/detail`, { state })
  }

  const exportDisabledReason = busy ? intl.formatMessage({ id: 'UrgencySummaryExportLoading' })
    : !hasData ? intl.formatMessage({ id: 'UrgencySummaryExportNoQuery' })
      : isEmpty ? intl.formatMessage({ id: 'UrgencySummaryExportEmpty' })
        : undefined

  /** Exporta lo que se ve: los filtros de la última consulta (no lo que se está editando), con los nombres en el encabezado. */
  const exportSummary = useCallback((format: ExportFormat) => {
    if (!applied) return Promise.reject(new Error('Resumen sin consultar'))
    const filtersText = intl.formatMessage({ id: 'UrgencySummaryExportFilters' }, {
      template: nameOf(options?.templates, applied.templateId) ?? intl.formatMessage({ id: 'UrgencySummaryAllFemale' }),
      financier: nameOf(options?.financiers, applied.financierId) ?? intl.formatMessage({ id: 'UrgencySummaryAllMale' }),
      priority: nameOf(options?.priorities, applied.priorityId) ?? intl.formatMessage({ id: 'UrgencySummaryAllMale' }),
    })
    return exportUrgencySummary(applied, format, filtersText)
  }, [applied, options, intl])

  const title = intl.formatMessage({ id: 'UrgencySummaryTitle' })

  if (status === 'denied') {
    return (
      <div className="nv-scope nv-page">
        <header className="nv-page-head">
          <h1 className="nv-h1">{title}</h1>
        </header>
        <p className="nv-note" role="status">
          <i className="pi pi-info-circle" aria-hidden="true" />
          {intl.formatMessage({ id: 'UrgencySummaryAccessDenied' })}
        </p>
      </div>
    )
  }

  const evolutionTitle = intl.formatMessage({
    id: measure === 'arrivals' ? 'UrgencySummaryEvolutionTitleArrivals' : 'UrgencySummaryEvolutionTitleWait',
  })

  return (
    <div className="nv-scope nv-page nv-urg-page">
      <NvTooltips />
      <div className="nv-page-head">
        <div className="nv-head-row">
          <h1 className="nv-h1">{title}</h1>
          <ExportMenu
            fallbackFileName="atenciones-de-urgencia"
            disabled={exportDisabledReason !== undefined}
            disabledReason={exportDisabledReason}
            onExport={exportSummary}
          />
        </div>
      </div>

      <UrgencyFilters data={data} />

      {rangeOrigin && (
        <div className="nv-urg-range-back">
          <Button
            type="button"
            link
            icon="pi pi-angle-double-left"
            label={intl.formatMessage({ id: 'UrgencySummaryReturnTo' }, { period: rangeOrigin.label })}
            onClick={returnToPreviousRange}
          />
        </div>
      )}

      <div className="nv-urg-results" aria-busy={busy}>
        {busy && (
          <div className="nv-urg-loading" role="status">
            <div className="nv-loading nv-urg-loading__box">
              <ProgressSpinner strokeWidth="4" style={{ width: 24, height: 24 }} />
              <span>{intl.formatMessage({ id: 'UrgencySummaryLoading' })}</span>
            </div>
          </div>
        )}

        {status === 'error' && !busy && (
          <div className="nv-card nv-urg-error" role="alert">
            <p className="nv-note">
              <i className="pi pi-info-circle" aria-hidden="true" />
              {errorMessage}
            </p>
            <Button type="button" className="buttonMain2" outlined label={intl.formatMessage({ id: 'UrgencySummaryRetry' })} onClick={retry} />
          </div>
        )}

        {isEmpty && (
          <div className={`nv-urg-no-results${busy ? ' nv-urg-blurred' : ''}`}>
            <p className="nv-muted">{intl.formatMessage({ id: 'UrgencySummaryEmpty' })}</p>
            {hasChips && (
              <Button
                type="button"
                link
                label={intl.formatMessage({ id: 'UrgencySummaryClearFilters' })}
                onClick={() => patchFilters({ templateId: null, financierId: null, priorityId: null })}
              />
            )}
          </div>
        )}

        {showContent && (
          <div className={`nv-urg-content${busy ? ' nv-urg-blurred' : ''}`}>
            <UrgencyIndicators summary={summary} />

            <UrgencyWhere
              rows={where.data ?? where.stale ?? []}
              status={where.status}
              refreshing={where.status === 'loading' && where.stale !== null}
              selectedTemplateId={applied.templateId}
              onSelectTemplate={(templateId) => patchFilters({ templateId })}
              onRetry={where.retry}
            />

            <div>
              <TabView activeIndex={tab} onTabChange={(event: TabViewTabChangeEvent) => onTabChange(event.index)}>
                <TabPanel header={intl.formatMessage({ id: 'UrgencySummaryTabEvolution' })}>
                  <section className="nv-section" aria-labelledby="nv-urg-evolution-title">
                    <div className="nv-urg-card-head">
                      <h2 className="nv-section-title" id="nv-urg-evolution-title">{evolutionTitle}</h2>
                      <UrgencyMeasureSelector value={measure} onChange={onMeasureChange} />
                    </div>
                    <UrgencyEvolution
                      daily={summary.daily}
                      measure={measure}
                      ariaLabel={evolutionTitle}
                      current={summary.current}
                      onOpenDay={openDay}
                    />
                  </section>
                </TabPanel>
                <TabPanel header={intl.formatMessage({ id: 'UrgencySummaryTabHourly' })}>
                  <UrgencyHourlyTab hourly={hourly} measure={measure} onMeasureChange={onMeasureChange} />
                </TabPanel>
                <TabPanel header={intl.formatMessage({ id: 'UrgencySummaryTabPriorities' })}>
                  <UrgencyPrioritiesTab priorities={priorities} />
                </TabPanel>
                <TabPanel header={intl.formatMessage({ id: 'UrgencySummaryTabGrouped' })}>
                  <section className="nv-section" aria-labelledby="nv-urg-group-title">
                    <h2 className="nv-sr-only" id="nv-urg-group-title">{intl.formatMessage({ id: 'UrgencySummaryGroupTitle' })}</h2>
                    <UrgencyGroupedTable
                      summary={summary}
                      groupBy={shownGroupBy}
                      onGroupByChange={onGroupByChange}
                      availableGroups={availableGroups}
                      onDrill={openDetail}
                    />
                  </section>
                </TabPanel>
              </TabView>
            </div>
          </div>
        )}
      </div>

      {showContent && dayReference && (
        <UrgencyDaySidebar
          day={detailDay}
          request={applied}
          measure={measure}
          reference={dayReference}
          templates={summary.byTemplate}
          activeDays={activeDays}
          showTemplate={byTemplate}
          onViewDay={viewDay}
          onHide={closeDetail}
        />
      )}
    </div>
  )
}
